import { useEffect, useState } from 'react'
import { DollarSign } from 'lucide-react'
import Modal from '../../../components/ERP/Modal/Modal'
import { patientService } from '../../../services/patientApi'
import { financialAccountService } from '../../../services/erpApi'
import { CHARGE_CATEGORY_API, todayInput } from './patientFormat'
import './PatientsPage.css'

function accountLabel(account) {
  const kind = account.account_type === 'digital' ? 'Digital' : account.account_type === 'physical' ? 'Física' : ''
  const code = account.code ? `${account.code} — ` : ''
  return `${code}${account.name}${kind ? ` (${kind})` : ''}`
}

function AddPaymentModal({ isOpen, patientId, patientName, onClose, onAdded }) {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('Diária / Leito')
  const [amount, setAmount] = useState('')
  const [financialAccountId, setFinancialAccountId] = useState('')
  const [accounts, setAccounts] = useState([])
  const [accountsError, setAccountsError] = useState('')
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [dueDate, setDueDate] = useState(todayInput())
  const [monthlyInstallments, setMonthlyInstallments] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!isOpen) return undefined
    let active = true
    setAccountsError('')
    financialAccountService.getAll({ active: true, limit: 200 })
      .then((response) => {
        if (!active) return
        const list = Array.isArray(response?.data) ? response.data : []
        setAccounts(list)
        setFinancialAccountId((current) => current || (list[0] ? String(list[0].id) : ''))
      })
      .catch(() => {
        if (active) setAccountsError('Não foi possível carregar as contas do extrato de caixa')
      })
    return () => {
      active = false
    }
  }, [isOpen])

  const submit = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      await patientService.addCharge(patientId, {
        description,
        category: CHARGE_CATEGORY_API[category],
        amount,
        financialAccountId,
        invoiceNumber,
        dueDate,
        monthlyInstallments
      })
      onAdded()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível lançar a cobrança')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Novo lançamento financeiro"
      subtitle="Faturamento e contas hospitalares"
      icon={<DollarSign size={22} />}
      size="medium"
      footer={(
        <button type="submit" form="payment-form" disabled={saving || accounts.length === 0}>
          {saving ? 'Salvando...' : 'Lançar no prontuário'}
        </button>
      )}
    >
      <form id="payment-form" onSubmit={submit}>
        {error && <p className="patients-alert-strip">{error}</p>}
        {accountsError && <p className="patients-alert-strip">{accountsError}</p>}
        <p className="patients-note">Paciente: <strong>{patientName}</strong></p>
        <div className="patients-form-grid patients-form-grid--2">
          <div className="patients-span-2">
            <label>Descrição do serviço / fatura *</label>
            <input required value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex: Diária de UTI + oxigenoterapia" />
          </div>
          <div>
            <label>Categoria</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.keys(CHARGE_CATEGORY_API).map((item) => <option key={item}>{item}</option>)}
            </select>
          </div>
          <div>
            <label>{monthlyInstallments ? 'Valor de cada parcela (R$) *' : 'Valor total (R$) *'}</label>
            <input required type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div className="patients-span-2">
            <label>Conta do extrato de caixa *</label>
            <select required value={financialAccountId} onChange={(e) => setFinancialAccountId(e.target.value)}>
              <option value="">Selecione a conta de destino</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>{accountLabel(account)}</option>
              ))}
            </select>
            {accounts.length === 0 && !accountsError && (
              <p className="patients-muted">Nenhuma conta ativa no extrato de caixa. Cadastre uma conta antes de lançar.</p>
            )}
          </div>
          <div>
            <label>Nº da fatura</label>
            <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
          </div>
          <div>
            <label>{monthlyInstallments ? 'Vencimento da 1ª parcela' : 'Vencimento'}</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <label className="patients-check-label patients-span-2">
            <input type="checkbox" checked={monthlyInstallments} onChange={(e) => setMonthlyInstallments(e.target.checked)} />
            Lançar 12 parcelas mensais
          </label>
          {monthlyInstallments && (
            <p className="patients-note patients-span-2">
              Serão criados 12 títulos, um por mês, a partir de {dueDate.split('-').reverse().join('/')}, na conta selecionada.
              {amount ? ` Total: ${(Number(amount) * 12).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}.` : ''}
            </p>
          )}
        </div>
      </form>
    </Modal>
  )
}

export default AddPaymentModal
